import { ActionPlay, hygieneDaysUntilDue, hygieneMonthsOverdue } from './queueRegistry'
import { ContactJson } from 'app/types/entities'

/**
 * The one fact on a board card that is not already said by the column it
 * sits in: why this particular contact, right now. Reuses the same reason
 * the focus card leads with (`actionPlayFor`'s result), so the two views
 * describe a contact the same way instead of inventing a second vocabulary
 * for it here.
 *
 * Hygiene plays carry no `detail` of their own; the focus card answers that
 * with a whole timeline rail (`HygieneTimeline`), which is too much for a
 * card. `hygieneDetail` reduces the same two numbers to one line instead.
 */
export function playDetail(play: ActionPlay, contact: ContactJson): string | undefined {
  if (play.detail) return play.detail(contact)
  if (play.timeline !== `hygiene`) return undefined
  return hygieneDetail(contact)
}

/**
 * How late (or how soon) a cleaning is, as one short line.
 *
 * Days, not months, drive the overdue branch. `hygieneMonthsOverdue` clamps
 * to 0 for anything under a full month late, so a patient 29 days overdue
 * reads identically to one not yet due if months is asked first. That was
 * the exact bug this function replaced, on the one queue (due-for-hygiene, a
 * 30-day window) where that distinction is the entire point of the board.
 * Days stay the readable unit up to 90 days late; past that the unit steps
 * up to months, and past two years to whole years, matching the thresholds
 * `HygieneTimeline` already uses on the focus card so a contact reads the
 * same age in both places.
 */
function hygieneDetail(contact: ContactJson): string | undefined {
  const daysOut = hygieneDaysUntilDue(contact)
  if (daysOut == null) return undefined
  if (daysOut > 0) return `Due in ${daysOut} ${daysOut === 1 ? `day` : `days`}`
  if (daysOut === 0) return `Due today`

  const daysLate = -daysOut
  if (daysLate < 90) return `${daysLate} ${daysLate === 1 ? `day` : `days`} overdue`

  const months = hygieneMonthsOverdue(contact)
  if (months == null) return `${daysLate} days overdue`
  if (months >= 24) return `${Math.floor(months / 12)} years overdue`
  return `${months} ${months === 1 ? `month` : `months`} overdue`
}
